<template>
  <div class="container my-4">
    <div class="card shadow-lg p-4">
      <!-- Form Row -->
      <div class="row g-3 align-items-end mb-4">
        <!-- SAP Invoice Dropdown -->
        <div class="col-md-4">
          <label for="sapDropdown" class="form-label fw-bold">Select SAP Invoice No</label>
          <select v-model="selectedSapInvoice" class="form-select" id="sapDropdown">
            <option value="" disabled>Select SAP Invoice</option>
            <option v-for="po in poList" :key="po.id" :value="po.id">
              {{ po.sap_po_no }}
            </option>
          </select>
        </div>

        <!-- PO Number Textbox -->
        <div class="col-md-4">
          <label for="poNo" class="form-label fw-bold">PO Number</label>
          <input
              type="text"
              id="poNo"
              class="form-control"
              :value="selectedPoNo"
              readonly
          />
        </div>

        <!-- Search Button -->
        <div class="col-md-4 d-flex">
          <button @click="performSearch" class="btn button-default align-self-end w-100">
            <i class="fa fa-search me-2"></i> Search
          </button>
        </div>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center mt-3">
        <i class="fa fa-spinner fa-spin text-primary"></i> Loading...
      </div>

      <!-- Dispatch Table -->
      <div v-if="gridData.length" class="mt-4">
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
            <tr>
              <th scope="col">Item Description</th>
              <th scope="col">Order Quantity</th>
              <th scope="col">GRN Quantity</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in gridData" :key="item.id">
              <td>{{ item.item_name }}</td>
              <td>{{ item.qty }}</td>
              <td>
                <input
                    type="number"
                    v-model="item.security_counted_qty"
                    class="form-control quantity-input"
                    min="0"
                    placeholder="0"
                />
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="btn btn-success px-4" @click="issueGatePass">
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { Grnallpolist, grnsearch, grnsave } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      poList: [],              // List of SAP invoices (with PO numbers too)
      selectedSapInvoice: null, // Selected SAP invoice ID
      selectedPoNo: "",         // PO number of selected SAP invoice
      gridData: [],             // Data for the grid
      loading: false,
    };
  },
  setup() {
    const router = useRouter();
    const navigateToNewDispatch = () => {
      router.push('/new-dispatch');
    };
    return { navigateToNewDispatch };
  },
  watch: {
    // Whenever selected SAP invoice changes, update PO number
    selectedSapInvoice(newVal) {
      const selected = this.poList.find(po => po.id === newVal);
      this.selectedPoNo = selected?.refrence_no || "";
    }
  },
  methods: {
    async fetchPoNumbers() {
      this.loading = true;
      try {
        const profileId = localStorage.getItem("profile_id");
        const token = localStorage.getItem("token_set");
        const response = await Grnallpolist(profileId, token);
        this.poList = response.data.data;
      } catch (error) {
        console.error("Error fetching PO list:", error);
      } finally {
        this.loading = false;
      }
    },

    async performSearch() {
      if (!this.selectedSapInvoice) {
        const toast = useToast();
        toast.error("Please select a SAP Invoice number!");
        return;
      }

      this.loading = true;
      try {
        const token = localStorage.getItem("token_set");
        const params = { purchase_order_id: this.selectedSapInvoice };
        const response = await grnsearch(params, token);
        this.gridData = response.data.data;
      } catch (error) {
        console.error("Error fetching grid data:", error);
      } finally {
        this.loading = false;
      }
    },

    async issueGatePass() {
      const toast = useToast();

      if (!this.gridData.length) {
        toast.error("No data to save. Please perform a search first.");
        return;
      }

      const token = localStorage.getItem("token_set");
      const profile_name = localStorage.getItem("profile_name");
      const profileId = localStorage.getItem("profile_id");

      const filteredGrnDetails = this.gridData
          .filter((item) => item.security_counted_qty != null && item.security_counted_qty !== "")
          .map((item) => ({
            item_id: item.id,
            qty: item.security_counted_qty,
          }));

      if (!filteredGrnDetails.length) {
        toast.error("No valid GRN details to save. Please provide quantities.");
        return;
      }

      const payload = {
        purchase_orders_id: this.selectedSapInvoice,
        sap_code: "SAP001",
        created_by: profileId,
        received_date: new Date().toISOString().split("T")[0],
        remarks: "TEST",
        total_value: "0",
        profile_name: profile_name,
        grn_details: filteredGrnDetails,
      };

      try {
        this.loading = true;
        const response = await grnsave(payload, token);
        if (response.data.status === "success") {
          toast.success("GRN data saved successfully!");
          this.gridData = [];
          this.$router.push('/grn');
        } else {
          toast.error(response.data.details || "Failed to save GRN data.");
        }
      } catch (error) {
        const errorMessage =
            error.response?.data?.message || error.message || "An error occurred while saving the data.";
        toast.error(errorMessage);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchPoNumbers();
  },
};
</script>

<style scoped>
.card {
  border-radius: 10px;
}
.table-striped tbody tr:nth-of-type(odd) {
  background-color: #f9f9f9;
}
.quantity-input {
  width: 80px;
  text-align: center;
}
.loading-spinner {
  font-size: 18px;
}
button {
  transition: all 0.3s ease-in-out;
}
button:hover {
  transform: scale(1.05);
}
/* Style readonly inputs as 'freezed' */
input[readonly] {
  background-color: #e9ecef; /* Light gray background */
  cursor: not-allowed;      /* Change cursor */
  color: #495057;           /* Text color */
  font-weight: bold;        /* Make it stand out */
}
</style>